import Promise from 'bluebird';
import _ from 'underscore';
import { useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService, getWizardData, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { getErrorsPlaceholders } from '@bingads-webui-campaign-react/xandr-campaign';
import biddingStrategyTypes from '@bingads-webui-campaign-legacy/bidding-strategy/bidding-strategy-type';
import { CampaignSubTypes } from '@bingads-webui-campaign-react/constants';
import { defaultBidForShoppingCampaign } from '@bingads-webui-campaign-legacy/budget/shared/defaultBid';

import {
  loadCampaign,
  loadAdGroup,
  modelToViewModelForAdGroup,
  saveAdGroup,
  saveCampaign,
} from '../../model';
import { isSearchShoppingCampaign } from '../step-utils';
import { useWizardLog, LOG_STATUS } from '../../shared';

export const useBudgetBidRepository = () => {
  const i18n = useContext(I18nContext);

  const serverContext = useContext(ServerContext);
  const scenarioContext = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const isPAAIMConsolidationEnabled = _.get(serverContext, ['DynamicPermissions', 'PAAIMConsolidation'], false);

  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;

  const { state, send } = useWizardService();
  const { flow } = getWizardData(state);
  const isAudienceShoppingCampaign = !isSearchShoppingCampaign(flow);
  const { onStepLoadedLog, onStepSavedLog } = useWizardLog({ activity, wizardState: state, isSearch: !isAudienceShoppingCampaign });

  const load = () => {
    onStepLoadedLog({ status: LOG_STATUS.LOAD_START });
    const savedCampaign = state.context.wizardData.campaign;
    const savedAdGroup = state.context.wizardData.adGroup;

    const adGroupId = savedAdGroup ? savedAdGroup.Id : null;
    const campaignId = savedCampaign ? savedCampaign.Id : null;

    const loadPromises = {
      adGroup: savedAdGroup ? Promise.resolve(savedAdGroup) : loadAdGroup(cid, aid, campaignId, adGroupId),
      campaign: savedCampaign ? Promise.resolve(savedCampaign) : loadCampaign(cid, aid, campaignId),
    };

    return Promise.props(loadPromises).then(({
      adGroup,
      campaign,
    }) => {
      onStepLoadedLog({ status: LOG_STATUS.LOAD_SUCCESS });
      const adGroupData = modelToViewModelForAdGroup({
        adGroupModel: {
          ...adGroup,
          SearchBid: adGroup.SearchBid !== undefined ? adGroup.SearchBid : defaultBidForShoppingCampaign(),
        },
        campaignModel: campaign,
        i18n,
        isAudienceShoppingCampaign,
      });

      return {
        adGroup: adGroupData.data,
        campaign,
        ...getErrorsPlaceholders(),
      };
    });
  };


  const save = ({ data }) => {
    onStepSavedLog({ status: LOG_STATUS.SAVE_START });
    const { adGroup, campaign } = data;
    campaign.Status = 'Paused';
    const isCostPerSaleEnabled = _.get(serverContext, ['Permissions', 'IsCostPerSaleEnabled'], false);

    if (campaign.BiddingScheme && campaign.BiddingScheme.Type === biddingStrategyTypes.costPerSale) {
      campaign.PersonalizedOffersEnabled = true;
      campaign.CashbackPercent = 10;
      campaign.CashbackScope = 'Product';
      campaign.Priority = 'High';
    } else if (isCostPerSaleEnabled && campaign.CampaignSubType !== CampaignSubTypes.AudienceDynamicFeedBased) {
      // PersonalizedOffersEnabled should be set to true/false only when CPS pilot is enabled, otherwise it will cause PersonalizedOffersAccountNotInPilot error.
      campaign.PersonalizedOffersEnabled = false;
      campaign.CashbackPercent = 'delete_value';
      campaign.CashbackScope = 'delete_value';
    } else {
      // This is for AudienceDynamicFeedBased campaign or when CPS pilot is not enabled.
      delete campaign.PersonalizedOffersEnabled;
      delete campaign.CashbackPercent;
      delete campaign.CashbackScope;
    }

    const saveAdGroupsPromise = () => saveAdGroup({
      adGroup: { data: adGroup },
      campaign,
      updateProductPartitionBids: true,
      isProductGroupAllowedForAudienceCampaign: isPAAIMConsolidationEnabled,
    }, {
      scenario: scenarioContext.name,
      activityLogger: scenarioContext.createActivity('saveAdGroup'),
      i18n,
      serverContext,
    });

    const saveCampaignPromise = () => saveCampaign({
      campaign: { data: campaign },
    }, {
      scenario: scenarioContext.name,
      activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
      i18n,
    });

    // Save campaign first, then save ad groups.
    // When change from CPS to some other bidding strategy,
    // we need to save campaign first, to prevent BidCannotBeManagedForBiddingScheme error in adgroup save.
    return saveCampaignPromise().then(campaignResult => saveAdGroupsPromise().then((adGroupResult) => {
      const campaignData = campaignResult.campaign.data;
      onStepSavedLog({ status: LOG_STATUS.SAVE_SUCCESS, msg: `Status: ${campaignData.Status}, Id: ${campaignData.Id}` });
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
        campaign: campaignData,
        adGroup: adGroupResult.adGroup.data,
      });

      return Promise.resolve({
        adGroup: adGroupResult.adGroup.data,
        campaign: campaignData,
        ...getErrorsPlaceholders(),
      });
    })).catch((error) => {
      const { serverErrors = {} } = error;
      onStepSavedLog({ status: LOG_STATUS.SAVE_FAIL, msg: serverErrors });
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ error: serverErrors });
    });
  };

  return {
    load,
    save,
  };
};
